/* eslint-disable react-native/no-inline-styles */
import Icon from 'design-system/src/Components/Icon/Icon';
import Typography from 'design-system/src/Components/Typography/Typography.web';
import useBreakpoint from 'design-system/src/WebComponent/Grid/hooks/useBreakpoint';
import VideoPlayerVertical from 'design-system/src/Widget/Player/VideoPlayer/VideoPlayerVertical.web';
import {darkTheme} from 'design-system/src/styles/Themes';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useTranslation} from 'gatsby-plugin-react-i18next';
import useTracker, {
  IPlayerPropertyName,
} from '@src/Services/Amplitude/hooks/useTracker';
import useSecuredBookmarks from '@src/Hooks/useSecuredBookmarks';
import useMember from '@src/Store/selectors/useMember';
import {useRegions} from '@src/Store/selectors/useRegions';
import {
  IPaywallFragmentFragment,
  useNewsByUrlEndUrlLazyQuery,
} from '@src/Api/Graphql/gql/types.generated';
import {useLazyGetVimeoVideoQuery} from '@src/Api/TraceApi/traceApi';
import {CustomActivityIndicator} from 'design-system/src/Components/CustomActivityIndicator';
import useBookmarkCount from '@src/Hooks/useBookmarkCount';
import checkVimeoUrl from '@src/Umbraco/Utils/CheckVimeoUrl';
import {RouteComponentProps} from '@reach/router';
import PaywallChecker from '@src/Umbraco/Components/PaywallChecker/PaywallChecker';
import useShareContent from '@src/Hooks/useShareContent';
import useViewPort from 'design-system/src/Hooks/useViewPort';
import useParams from '@src/Hooks/useParams';
import {PlayerPaywallTrackingProps} from '@src/Services/Amplitude/hooks/tracker';
import usePaywall from '@src/Hooks/usePaywall';
import {useVerticalVideoPlayerTracking} from './hooks/useVerticalVideoPlayerTracking';

import styled from 'styled-components';
import AppVideoPlayerVerticalLayout from './components/AppVideoPlayerVerticalLayout/AppVideoPlayerVerticalLayout';

function getLoadingComponent({
  isLoadingAny,
  isNoVideo,
  viewportHeight,
  t,
}: {
  isLoadingAny: boolean;
  isNoVideo: boolean;
  viewportHeight?: number;
  t: (key: string) => string;
}) {
  if (!isLoadingAny && !isNoVideo) return null;
  return (
    <LoadingContainer $viewportHeight={viewportHeight}>
      {isLoadingAny ? (
        <CustomActivityIndicator />
      ) : (
        <Typography variant="h2">{t('No video found')}</Typography>
      )}
    </LoadingContainer>
  );
}

const LoadingContainer = styled.div<{$viewportHeight?: number}>`
  width: 100%;
  height: ${({$viewportHeight}) =>
    $viewportHeight ? `${$viewportHeight}px` : '100%'};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const AppVideoPlayerVerticale: FC<
  RouteComponentProps<{id: string}>
> = props => {
  // 1. External hooks (order: params, translation, redux/selectors, tracker, viewport, etc.)
  const {endUrl} = useParams();
  const {t} = useTranslation('translation');
  const {getPremiumPreviewSettings} = usePaywall();
  const tracker = useTracker();
  const {isMobile} = useBreakpoint();
  const {viewportHeight} = useViewPort();
  const {isBookmarked, simpleToggle} = useSecuredBookmarks();
  const member = useMember();
  const {findRegionsByDocumentUrl} = useRegions();
  const {fetchContentLike, bookmarkCount = {}} = useBookmarkCount();
  const shareContent = useShareContent();

  // 2. GraphQL/data fetching hooks
  const [fetchNews, {data, loading}] = useNewsByUrlEndUrlLazyQuery();
  const [fetchVimeoVideo, {isLoading: loadingVideo}] =
    useLazyGetVimeoVideoQuery();

  // 3. State hooks
  const [isOpenDialogShare, setIsOpenDialogShare] = useState(false);
  const [isLoadingVideoUri, setIsLoadingVideoUri] = useState(true);
  const [videoUrl, setVideoUrl] = useState<string>();
  const [loadingComponent, setLoadingComponent] = useState(true);
  const [watchTime, setWatchTime] = useState(0);
  const [originalInfo, setOriginalInfo] = useState<
    | {
        originalLanguage?: string;
        originalQuality?: string;
        contentTimestamp: Date;
        loadTime?: number;
      }
    | undefined
  >();

  // 4. Refs
  const lastTimeRef = useRef<number>(0);
  const refCurrentTime = useRef<number>(0);
  const refCurrentDuration = useRef<number>(0);

  // 5. Derived data
  const news = data?.allNews?.items[0];
  const id = news?.id;

  const isLoadingAny =
    loadingComponent || loading || loadingVideo || isLoadingVideoUri;
  const isNoVideo = !videoUrl && !isLoadingAny;
  const loadingComponentNode = getLoadingComponent({
    isLoadingAny,
    isNoVideo,
    viewportHeight,
    t,
  });

  const trackingData: PlayerPaywallTrackingProps = useMemo(() => {
    const premiumPreviewSettings = getPremiumPreviewSettings(
      news?.paywall as IPaywallFragmentFragment,
    );
    const maxPreview = premiumPreviewSettings.previewDurationVod;
    const trackingData: PlayerPaywallTrackingProps = {
      id: news?.id || '',
      title: news?.title || '',
      __typename: 'News',
      maxPreviewDurationMinutes: maxPreview,
      watchedTimeSeconds3Decimals: watchTime,
    };

    return trackingData;
  }, [watchTime, news]);

  const getContentProperties = useCallback((): IPlayerPropertyName => {
    const content = news;
    if (content) {
      const {parent, interests, musicGenre} = content;
      const properties: IPlayerPropertyName = {
        id: news.id,
        title: news.title ?? '',
        typename: news.__typename,
        category:
          parent && parent.__typename === 'NewsCateg' && parent.name
            ? parent.name
            : '',
        length: '',
        contentRegion:
          (member &&
            member.region &&
            findRegionsByDocumentUrl(member.region)?.name) ||
          '',
        interestName:
          interests && interests[0] && interests[0].__typename === 'ContentTag'
            ? interests[0].name
            : '',
        contentTimestamp: originalInfo?.contentTimestamp,
        //TO DO
        // source: (params.source as string) || '',
        originalLanguage: originalInfo?.originalLanguage,
        originalQuality: originalInfo?.originalQuality,
        musicGenre: musicGenre
          ? musicGenre.map((e: any) => e?.name).join(',')
          : '',
        loadTime: originalInfo?.loadTime || 0,
        restrictedCountries:
          (content as any).countriesRestriction &&
          Array.isArray((content as any).countriesRestriction)
            ? (content as any).countriesRestriction.map((v: any) => {
                const [country] = (v || '').split('|');
                return country ? country.trim() : '';
              })
            : '',
      };

      return properties;
    }

    return {
      id: '',
      title: '',
      typename: '',
      musicGenre: '',
      length: '',
      category: '',
      loadTime: 0,
      contentRegion: '',
    };
  }, [originalInfo, news]);

  const {onTimeUpdate, onPlayEnd, onVideoPlay, onVideoPause} =
    useVerticalVideoPlayerTracking({
      tracker,
      getContentProperties,
      refCurrentDuration,
      watchTime,
      setWatchTime,
      lastTimeRef,
      news,
    });

  useEffect(() => {
    if (endUrl) {
      setLoadingComponent(false);
      fetchNews({
        variables: {
          endUrl: endUrl,
        },
      });
    }
  }, [endUrl]);

  useEffect(() => {
    if (id && !bookmarkCount[id]) {
      fetchContentLike({
        contentId: id,
      });
    }
  }, [id, bookmarkCount]);

  const count: number = news && news.id ? bookmarkCount[news.id] : 0;

  useEffect(() => {
    setIsLoadingVideoUri(true);
    if (news && news.vimeoId) {
      if (news.vimeoHlsUrl && checkVimeoUrl(news.vimeoHlsUrl)) {
        if (videoUrl !== news.vimeoHlsUrl) {
          setVideoUrl(news.vimeoHlsUrl);
        }
        setIsLoadingVideoUri(false);
      } else {
        fetchVimeoVideo(news.vimeoId + '')
          .then(({data: videoData}) => {
            if (videoData && videoData.hls && videoUrl !== videoData.hls) {
              setVideoUrl(videoData.hls);
            }
          })
          .finally(() => {
            setIsLoadingVideoUri(false);
          });
      }
    } else {
      setIsLoadingVideoUri(false);
    }
  }, [fetchVimeoVideo, news, tracker.general, videoUrl]);

  useEffect(
    function trackContentDetailPageViewed() {
      if (news) {
        tracker.general.contentDetailPageViewed({
          content_id: news.id,
          content_title: news.title || '',
          content_type: news.__typename,
          genre: news.parent?.name || '',
          // TODO : source
          source: 'browse',
        });
      }
    },
    [news, tracker.general],
  );

// Pour éviter que le cleanup s'exécute à chaque update, on utilise une ref pour garder la dernière version des propriétés
const lastContentPropertiesRef = useRef(getContentProperties());
useEffect(() => {
  lastContentPropertiesRef.current = getContentProperties();
}, [getContentProperties]);

useEffect(() => {
  return () => {
    tracker.player.closed(lastContentPropertiesRef.current);
  };
}, []);

  const isLiked = news && news.id ? isBookmarked(news.id) : false;

  const handleOnVideoLoad = (
    _data: React.SyntheticEvent<HTMLVideoElement, Event>,
    originalQuality?: number,
    loadTime?: number,
  ) => {
    setOriginalInfo({
      originalLanguage: '', // TODO: Get original language if available
      originalQuality: originalQuality ? originalQuality + '' : '',
      contentTimestamp: new Date(), // Wtf: Pas sûr que ça ait du sens ça
      loadTime,
    });
  };

  const handleOnTimeProgress = useCallback((_currentTime: number) => {
    refCurrentTime.current = _currentTime;
  }, []);

  const onGetDuration = (duration: number) =>
    (refCurrentDuration.current = duration);

  const layoutProps = {
    news,
    isMobile,
    viewportHeight,
    videoUrl,
    isLiked,
    count,
    handleOnVideoLoad,
    handleOnTimeProgress,
    onPlayEnd,
    onGetDuration,
    onTimeUpdate,
    onVideoPlay,
    onVideoPause,
    simpleToggle,
    shareContent,
    isOpenDialogShare,
    setIsOpenDialogShare,
    t,
  };

  return !loadingComponentNode ? (
    <PaywallChecker
      accessType={news?.accessType}
      paywall={news?.paywall}
      trackingData={trackingData}>
      <AppVideoPlayerVerticalLayout {...layoutProps} />
    </PaywallChecker>
  ) : (
    loadingComponentNode
  );
};

export default AppVideoPlayerVerticale;
